import * as React from 'react';
import {Helmet} from 'react-helmet';
import {generatePath} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter, RouterHistory} from 'react-router-dom';

import {useMutation} from '@apollo/react-hooks';

import type {FilterObject} from 'nutshell-core/types';

import {headTitle} from 'shells/document/head-title';
import {ErrorState} from 'shells/error-state/error-state';

import type {
    EditionUpdateInput,
    EditionFragment as Edition,
    EditionTestEmailSend,
    EditionTestEmailSendVariables,
    EditionStubFragment as EditionStub,
} from 'nutshell-graphql-types';

import type {ReportURLParams} from '../../reports/report-types';

import {EditionMutations} from '../graphql-wrappers/edition/edition-mutations';
import {EditionQuery} from '../graphql-wrappers/edition/edition-query';

import {EditionPage} from './edition-page';
import EDITION_TEST_EMAIL_SEND from './graphql/mutations/edition-test-email-send.graphql';
import {SavingDebounce} from './utils/saving-debounce';

type OwnProps = ContextRouter;

type Props = OwnProps & {
    history: RouterHistory;
    edition: Edition | null | undefined;
    editionStub: EditionStub | null | undefined;
    error: string | null | undefined;
    isLoading: boolean;
    onSendEmail: (toEmailAddress: string[]) => Promise<any>;
    onChange: (editionId: string, beeBodyJson: string) => Promise<any>;
    onEditionSave: (saveData: EditionUpdateInput) => Promise<any>;
    isSaving: boolean;
    savingError?: string;
    activeFilters: FilterObject[];
    onRemoveAllFilters: () => void;
    onParamChange: (arg1: any, arg2: boolean) => any;
    onParamPush: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
};

export function EditionPageWrapperComponent(props: Props) {
    const setSelectedEditorTab = (newTab: string) => {
        const newPath = generatePath(props.match.path, {
            editionId: props.match.params.editionId,
            newsletterId: props.match.params.newsletterId,
            tab: newTab,
        });

        // Make sure to pass the search here, because without it we force a
        // rerender, which causes BEE to reload.
        props.history.replace({pathname: newPath, search: props.location.search});
    };

    const pageTitle = props.edition
        ? `${props.edition.campaign.name} • ${props.edition.name}`
        : 'Edition';

    return (
        <>
            <Helmet>
                <title>{headTitle(pageTitle)}</title>
            </Helmet>
            {props.error ? (
                <ErrorState errorMessage={props.error} />
            ) : (
                <EditionPage
                    {...props}
                    onSetSelectedEditorTab={setSelectedEditorTab}
                    campaignType='NEWSLETTER'
                    generatePath={(newTab: string) => {
                        const path = generatePath(props.match.path, {
                            editionId: props.match.params.editionId,
                            newsletterId: props.match.params.newsletterId,
                            tab: newTab,
                        });

                        return props.location.search && props.location.search.length
                            ? `${path}${props.location.search}`
                            : path;
                    }}
                />
            )}
        </>
    );
}

export function EditionPageWrapper(props: OwnProps) {
    const editionIdParam = props.match.params.editionId;
    const editionApiId = editionIdParam ? editionIdParam : '';

    const [sendTestEmail] = useMutation<EditionTestEmailSend, EditionTestEmailSendVariables>(
        EDITION_TEST_EMAIL_SEND
    );

    return (
        <EditionQuery editionId={editionApiId}>
            {({edition, editionStub, isLoading, error}) => (
                <EditionMutations>
                    {({onUpdate}) => (
                        <SavingDebounce
                            onSave={(saveData) => {
                                return onUpdate(saveData);
                            }}
                        >
                            {({onChange, isSaving, hasChanged}) => (
                                <EditionPageWrapperComponent
                                    {...props}
                                    edition={edition}
                                    editionStub={editionStub}
                                    isLoading={isLoading}
                                    error={error}
                                    // The behavior in this case is the same if the content has
                                    // changed or if we're saving
                                    isSaving={isSaving || hasChanged}
                                    // @ts-expect-error
                                    onSendEmail={(emailAddresses) => {
                                        const editionId = edition && edition.id;
                                        if (editionId) {
                                            return sendTestEmail({
                                                variables: {
                                                    input: {
                                                        editionId: editionId,
                                                        emailAddresses: emailAddresses,
                                                    },
                                                },
                                            });
                                        }
                                    }}
                                    onChange={(editionId: string, beeBodyJson: string) => {
                                        onChange({editionId, beeBodyJson});
                                    }}
                                    onEditionSave={onUpdate}
                                />
                            )}
                        </SavingDebounce>
                    )}
                </EditionMutations>
            )}
        </EditionQuery>
    );
}
